// Overview money ledgers (pure): colour marks signed deltas only; zero reads muted.
import { fmt, fmt_money, fmt_money_signed, fmt_signed, num } from '../format';

const missing = (v) => v === null || v === undefined || v === '';
// paise: always two decimals (the L2 breakdown); otherwise paise only when present.
const plain = (n, paise) => (paise ? fmt(n, 2) : fmt_money(n));
const signed = (n, paise) => (paise ? fmt_signed(n, 2) : fmt_money_signed(n));

// An addition: "+₹450" in the positive tone; a negative one keeps its own sign and tone.
export const addition_cell = (v, paise = false) => {
  if (missing(v)) return { value: fmt_money(v), tone: 'zero' };
  const n = num(v);
  if (n === 0) return { value: plain(0, paise), tone: 'zero' };
  return { value: signed(n, paise), tone: n > 0 ? 'pos' : 'neg' };
};

// A deduction sent positive (penalties) reads "−₹250"; a negative one is a credit, "+₹250".
export const deduction_cell = (v, paise = false) => {
  if (missing(v)) return { value: fmt_money(v), tone: 'zero' };
  const n = num(v);
  if (n === 0) return { value: plain(0, paise), tone: 'zero' };
  return n > 0
    ? { value: plain(-n, paise), tone: 'neg' }
    : { value: signed(-n, paise), tone: 'pos' };
};

// A plain amount (earned, informational): neutral, negative in the negative tone, zero muted.
export const plain_cell = (v, paise = false) => {
  if (missing(v)) return { value: fmt_money(v), tone: 'zero' };
  const n = num(v);
  if (n === 0) return { value: plain(0, paise), tone: 'zero' };
  return { value: plain(n, paise), tone: n < 0 ? 'neg' : '' };
};

// Information only (waived, Free Pass, corrections): never charged, so never signed or coloured.
export const info_cell = (v, paise = false) => {
  if (missing(v)) return { value: fmt_money(v), tone: 'zero' };
  return { value: plain(num(v), paise), tone: 'zero' };
};

// An Ops correction that raised the penalty is already charged (signed); one that lowered it is information.
export const correction_cells = (v, paise = false) => {
  const n = num(v);
  if (missing(v) || n >= 0) return { raised: null, not_charged: info_cell(v, paise) };
  return { raised: { value: signed(-n, paise), tone: '' }, not_charged: null };
};

// An earning: neutral, muted when zero; incentives carry a "+" like the mock.
const earning_cell = (v, with_sign = false) => {
  if (missing(v)) return { value: fmt_money(v), tone: 'zero' };
  const n = num(v);
  if (n === 0) return { value: fmt_money(0), tone: 'zero' };
  return { value: with_sign ? fmt_money_signed(n) : fmt_money(n), tone: '' };
};

export const earned_items = (r) => [
  { label: '1-on-1', ...earning_cell(r.one_on_one_amount) },
  { label: 'Group classes', ...earning_cell(r.group_class_amount) },
  { label: 'Trial', ...earning_cell(r.trial_amount) },
  { label: 'Incentives', ...earning_cell(r.incentive_amount, true) },
  // Encashment is in gross (goserver totals.go), so it is listed whenever present.
  ...(num(r.encashment_amount) !== 0
    ? [{ label: 'Free Pass encashment', ...earning_cell(r.encashment_amount, true) }]
    : [])
];

// The signed lines between earned and gross (penalty_amount is only what was charged).
export const adjustment_items = (r) => [
  { label: 'Additional', ...addition_cell(r.additional_amount) },
  { label: 'Penalties', ...deduction_cell(r.penalty_amount) }
];

export const NOT_CHARGED = 'Not charged';

// The not-charged sub-ledger: all zero collapses to one muted line; a non-zero value is always listed.
export const not_charged_view = (entries, paise = false) => {
  const list = entries.filter(Boolean);
  if (list.every((e) => num(e.amount) === 0)) {
    return { collapsed: true, items: [{ label: NOT_CHARGED, ...info_cell(0, paise) }] };
  }
  return {
    collapsed: false,
    items: list.map((e) => ({ label: e.label, ...info_cell(e.amount, paise) }))
  };
};

// Not part of gross: shown for information under the signed lines.
export const adjustment_info_view = (r) =>
  not_charged_view([{ label: 'Waived', amount: r.waived_penalty_amount }]);

const paise_of = (v) => Math.round(num(v) * 100);

// Gross to Payable (GST, TDS, a deficit carried out, rupee rounding) so the shown lines sum to Payable.
export const payable_bridge_items = (r) => {
  if (missing(r.gross_amount) || missing(r.tds_amount) || missing(r.payable_amount)) return [];
  const items = [];
  if (num(r.gst_amount) !== 0) items.push({ label: 'GST', ...addition_cell(r.gst_amount) });
  items.push({ label: 'TDS', ...deduction_cell(r.tds_amount) });
  const carry = paise_of(r.carry_forward_amount);
  if (carry > 0) {
    items.push({
      label: 'Deficit carried to next month',
      value: fmt_money_signed(carry / 100),
      tone: ''
    });
  }
  const shown =
    paise_of(r.gross_amount) + paise_of(r.gst_amount) - paise_of(r.tds_amount) + Math.max(carry, 0);
  const rounding = paise_of(r.payable_amount) - shown;
  // Rupee rounding is under ₹1; a bigger gap is not rounding, so it is never labelled as such.
  if (rounding !== 0 && Math.abs(rounding) < 100) {
    items.push({ label: 'Rounding', value: fmt_money_signed(rounding / 100), tone: '' });
  }
  return items;
};
